import React, { useState, useContext, useRef, useEffect } from "react";
import { X, ChevronDown, Delete, DeleteIcon, Trash } from "lucide-react";
import {
  createPet,
  addMedicalHistory,
  createPetWithMedical,
} from "../../../services/api";
import { AuthContext } from "../../../context/AuthContext";

const AddPetModal = ({ isOpen, onClose, onSuccess, onError }) => {
  const { userDetails, getUserDetails } = useContext(AuthContext);
  const { loginUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [isBreedDropdownOpen, setIsBreedDropdownOpen] = useState(false);
  const [filteredBreeds, setFilteredBreeds] = useState([]);
  const breedDropdownRef = useRef(null);
  const [allergies, setAllergies] = useState("");
  const [vaccinations, setVaccinations] = useState([{ name: "", date: "" }]);
  const [dewormings, setDewormings] = useState([{ name: "", date: "" }]);
  const [neuteredEntries, setNeuteredEntries] = useState([
    { status: "", date: "" },
  ]);

  console.log("AddPetModal userDetails:", userDetails);

  const handleAddVaccination = () => {
    setVaccinations([...vaccinations, { name: "", date: "" }]);
  };

  const handleAddDeworming = () => {
    setDewormings([...dewormings, { name: "", date: "" }]);
  };

  const handleNeuteredStatusChange = (index, value) => {
    const updated = [...neuteredEntries];
    updated[index].status = value;
    if (value === "no") {
      updated[index].date = "";
    }
    setNeuteredEntries(updated);
  };

  const handleAddNeutered = () => {
    setNeuteredEntries([...neuteredEntries, { status: "", date: "" }]);
  };

  const handleDeleteVaccination = (index) => {
    if (vaccinations.length > 1) {
      const updated = vaccinations.filter((_, i) => i !== index);
      setVaccinations(updated);
    }
  };

  const handleDeleteDeworming = (index) => {
    if (dewormings.length > 1) {
      const updated = dewormings.filter((_, i) => i !== index);
      setDewormings(updated);
    }
  };

  const handleDeleteNeutered = (index) => {
    if (neuteredEntries.length > 1) {
      const updated = neuteredEntries.filter((_, i) => i !== index);
      setNeuteredEntries(updated);
    }
  };

  // Form state
  const [petForm, setPetForm] = useState({
    name: "",
    species: "",
    breed: "",
    age: "",
    weight: "",
    gender: "",
    color: "",
  });

  // Breed options based on species
  const breedOptions = {
    Dog: [
      "Indian Desi (Non Descript)",
      "Labrador Retriever",
      "German Shepherd",
      "Golden Retriever",
      "Doberman Pinscher",
      "Pomeranian",
      "Pug",
      "Shih Tzu",
    ],
    Cat: [
      "Indian Bill (Desi Cat)",
      "Persian",
      "Moggie (Cross breed)",
      "Bengal Cat",
      "Bombay",
      "Maine Coon",
      "Siamese",
      "Himalayan Cat",
    ],
    Rabbit: [
      "Soviet Chinchilla",
      "New Zealand White",
      "White Giant",
      "Grey Giant",
    ],
    Bird: ["Cockatiels", "Budgies (Parakeet)", "Love Birds"],
    Fish: [
      "Goldfish",
      "Betta (Siamese Fighting Fish)",
      "Guppy",
      "Molly",
      "Platy",
      "Zebra Danio",
      "Tetra (Neon, Glowlight)",
      "Angelfish",
    ],
  };

  const resetForm = () => {
    setPetForm({
      name: "",
      species: "",
      breed: "",
      age: "",
      weight: "",
      gender: "",
      color: "",
    });

    // Reset medical details
    setAllergies("");
    setVaccinations([{ name: "", date: "" }]);
    setDewormings([{ name: "", date: "" }]);
    setNeuteredEntries([{ status: "", date: "" }]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSpeciesChange = (e) => {
    const selectedSpecies = e.target.value;
    setPetForm({
      ...petForm,
      species: selectedSpecies,
      breed: "", // Reset breed when species changes
    });
    setIsBreedDropdownOpen(false);
    setFilteredBreeds([]);
  };

  const handleBreedChange = (e) => {
    const value = e.target.value;
    setPetForm({ ...petForm, breed: value });

    // Filter breeds based on input
    if (breedOptions[petForm.species] && value) {
      const filtered = breedOptions[petForm.species].filter((breed) =>
        breed.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredBreeds(filtered);
      setIsBreedDropdownOpen(filtered.length > 0);
    } else if (breedOptions[petForm.species]) {
      setFilteredBreeds(breedOptions[petForm.species]);
      setIsBreedDropdownOpen(true);
    }
  };

  const handleBreedSelect = (breed) => {
    setPetForm({ ...petForm, breed });
    setIsBreedDropdownOpen(false);
    setFilteredBreeds([]);
  };

  const handleBreedInputFocus = () => {
    if (breedOptions[petForm.species]) {
      setFilteredBreeds(breedOptions[petForm.species]);
      setIsBreedDropdownOpen(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        breedDropdownRef.current &&
        !breedDropdownRef.current.contains(event.target)
      ) {
        setIsBreedDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async () => {
    // Validate form
    if (
      !petForm.name ||
      !petForm.species ||
      !petForm.breed ||
      !petForm.dateOfBirth ||
      !petForm.weight ||
      !petForm.gender ||
      !petForm.color
    ) {
      onError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);

      // Combine pet data and medical data
      const combinedData = {
        ...petForm,
        age: parseInt(petForm.dateOfBirth),
        weight: parseFloat(petForm.weight),
        ownerId: loginUser.id,
        // Medical data
        allergies: allergies || "",
        vaccinations: vaccinations.filter((vax) => vax.name.trim() || vax.date),
        dewormings: dewormings.filter(
          (entry) => entry.name.trim() || entry.date
        ),
        spayedNeutered: neuteredEntries.filter((entry) => entry.status),
      };

      // Create pet with medical details
      const response = await createPet(combinedData);

      if (response.success) {
        resetForm();
        onSuccess("Pet and medical details added successfully!");
        onClose();
      } else {
        onError(response.message || "Failed to add pet");
      }
    } catch (err) {
      onError(err.message || "Failed to add pet");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Add New Pet</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pet Name *
                </label>
                <input
                  type="text"
                  value={petForm.name}
                  onChange={(e) =>
                    setPetForm({ ...petForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter pet name"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Species *
                </label>
                <select
                  value={petForm.species}
                  onChange={handleSpeciesChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                >
                  <option value="">Select species</option>
                  <option value="Dog">Dog</option>
                  <option value="Cat">Cat</option>
                  <option value="Rabbit">Rabbit</option>
                  <option value="Bird">Pet Birds</option>
                  <option value="Fish">Fish</option>
                  
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Breed *
                </label>
                {breedOptions[petForm.species] ? (
                  <div className="relative" ref={breedDropdownRef}>
                    <div className="relative">
                      <input
                        type="text"
                        value={petForm.breed}
                        onChange={handleBreedChange}
                        onFocus={handleBreedInputFocus}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Select breed or type custom"
                        disabled={loading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (isBreedDropdownOpen) {
                            setIsBreedDropdownOpen(false);
                          } else {
                            handleBreedInputFocus();
                          }
                        }}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                        disabled={loading}
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isBreedDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>

                    {isBreedDropdownOpen && filteredBreeds.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredBreeds.map((breed, index) => (
                          <button
                            key={breed}
                            type="button"
                            onClick={() => handleBreedSelect(breed)}
                            className={`w-full px-3 py-2 text-left hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150 ${
                              index === 0 ? "rounded-t-lg" : ""
                            } ${
                              index === filteredBreeds.length - 1
                                ? "rounded-b-lg"
                                : ""
                            } ${
                              petForm.breed === breed
                                ? "bg-blue-100 text-blue-700"
                                : "text-gray-700"
                            }`}
                            disabled={loading}
                          >
                            {breed}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={petForm.breed}
                    onChange={handleBreedChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter breed"
                    disabled={loading}
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  value={petForm.dateOfBirth}
                  onChange={(e) =>
                    setPetForm({ ...petForm, dateOfBirth: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Select date of birth"
                  disabled={loading}
                  required
                  max={new Date().toISOString().split("T")[0]} // Prevents selecting future dates
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight (kg) *
                </label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  step="0.1"
                  value={petForm.weight}
                  onChange={(e) =>
                    setPetForm({ ...petForm, weight: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter weight"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <select
                  value={petForm.gender}
                  onChange={(e) =>
                    setPetForm({ ...petForm, gender: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color *
                </label>
                <input
                  type="text"
                  value={petForm.color}
                  onChange={(e) =>
                    setPetForm({ ...petForm, color: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter pet color"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className=" max-w-full overflow-hidden">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                Add Medical Details
              </h2>

              {/* Allergies */}
              <div className="mb-6">
                <label className="block font-medium text-gray-700 mb-1">
                  History of any previous disease or allergies?
                </label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded text-sm sm:text-base"
                  placeholder="Enter details here"
                  value={allergies}
                  onChange={(e) => setAllergies(e.target.value)}
                />
              </div>

              {/* Vaccination Section */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-2">
                  Vaccination Details
                </label>

                {vaccinations.map((vax, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3 md:mb-2"
                  >
                    <input
                      type="text"
                      className="w-full md:flex-1 p-2 border border-gray-300 rounded text-sm sm:text-base"
                      placeholder="Which vaccination?"
                      value={vax.name}
                      onChange={(e) => {
                        const updated = [...vaccinations];
                        updated[index].name = e.target.value;
                        setVaccinations(updated);
                      }}
                    />
                    <input
                      type="date"
                      className="w-full md:w-40 p-2 border border-gray-300 rounded text-sm sm:text-base"
                      value={vax.date}
                      onChange={(e) => {
                        const updated = [...vaccinations];
                        updated[index].date = e.target.value;
                        setVaccinations(updated);
                      }}
                    />
                    {vaccinations.length > 1 && (
                      <button
                        onClick={() => handleDeleteVaccination(index)}
                        className="w-full md:w-auto px-3 py-2 text-xs sm:text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        type="button"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddVaccination}
                    className="px-3 py-1 text-xs sm:px-4 sm:py-1 sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    + Add Vaccination
                  </button>
                </div>
              </div>

              {/* Deworming Section */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-2">
                  Deworming Details
                </label>

                {dewormings.map((entry, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3 md:mb-2"
                  >
                    <input
                      type="text"
                      className="w-full md:flex-1 p-2 border border-gray-300 rounded text-sm sm:text-base"
                      placeholder="Which deworming?"
                      value={entry.name}
                      onChange={(e) => {
                        const updated = [...dewormings];
                        updated[index].name = e.target.value;
                        setDewormings(updated);
                      }}
                    />
                    <input
                      type="date"
                      className="w-full md:w-40 p-2 border border-gray-300 rounded text-sm sm:text-base"
                      value={entry.date}
                      onChange={(e) => {
                        const updated = [...dewormings];
                        updated[index].date = e.target.value;
                        setDewormings(updated);
                      }}
                    />
                    {dewormings.length > 1 && (
                      <button
                        onClick={() => handleDeleteDeworming(index)}
                        className="w-full md:w-auto px-3 py-2 text-xs sm:text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        type="button"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddDeworming}
                    className="px-3 py-1 text-xs sm:px-4 sm:py-1 sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    + Add Deworming
                  </button>
                </div>
              </div>

              {/* Neutering Section - Mobile Responsive */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-2">
                  Spayed / Neutered
                </label>

                {neuteredEntries.map((entry, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3 md:mb-2"
                  >
                    <div className="w-full md:flex-1 flex items-center gap-4 md:gap-6">
                      <label className="flex items-center gap-1 text-sm sm:text-base">
                        <input
                          type="radio"
                          name={`neutered-${index}`}
                          value="yes"
                          checked={entry.status === "yes"}
                          onChange={() =>
                            handleNeuteredStatusChange(index, "yes")
                          }
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-1 text-sm sm:text-base">
                        <input
                          type="radio"
                          name={`neutered-${index}`}
                          value="no"
                          checked={entry.status === "no"}
                          onChange={() =>
                            handleNeuteredStatusChange(index, "no")
                          }
                        />
                        No
                      </label>
                    </div>
                    <input
                      type="date"
                      className={`w-full md:w-40 p-2 border border-gray-300 rounded text-sm sm:text-base ${
                        entry.status !== "yes"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }`}
                      value={entry.date}
                      disabled={entry.status !== "yes"}
                      onChange={(e) => {
                        const updated = [...neuteredEntries];
                        updated[index].date = e.target.value;
                        setNeuteredEntries(updated);
                      }}
                    />
                    {neuteredEntries.length > 1 && (
                      <button
                        onClick={() => handleDeleteNeutered(index)}
                        className="w-full md:w-auto px-3 py-2 text-xs sm:text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        type="button"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                {/* <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddNeutered}
                    className="px-3 py-1 text-xs sm:px-4 sm:py-1 sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    + Add Spay/Neuter
                  </button>
                </div> */}
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleClose}
                disabled={loading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Adding..." : "Add Pet"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPetModal;
